interface ValueFeature {
  properties?: { value?: number | null } | null;
}

// #0000 — Market/City/ZIP adopt the same orange density scale as the State
// choropleth (team decision, Unified Reporting readiness review): these 5
// stops are sampled off the exact gradient MapLegend already renders
// (#fff5eb -> #fd8d3c -> #7f2704, matching UsChoropleth's `interpolate:
// 'oranges'`), so the legend — which was already orange — finally matches
// the fill it's labelling instead of the old grey/green ramp.
const RAMP_COLORS = ['#fff5eb', '#fec194', '#fd8d3c', '#be5a20', '#7f2704'];

/**
 * Quintile stops across positive values, not linear min/max — a linear ramp
 * collapses a skewed distribution (one big market, many small ones) into a
 * single visible band, invisible against the basemap for ~98% of features.
 * Percentile stops give each colour band ~20% of features regardless of skew
 * (same fix the legacy Mapbox choropleth already uses).
 */
const getStops = (features: ValueFeature[]): number[] | null => {
  const positives = features
    .map(f => Number(f.properties?.value))
    .filter(n => Number.isFinite(n) && n > 0)
    .sort((a, b) => a - b);
  if (!positives.length) return null;
  const at = (p: number) => positives[Math.min(positives.length - 1, Math.floor(positives.length * p))];
  // Mapbox requires strictly ascending stops — nudge ties up by 1 so a
  // mostly-identical dataset still produces a valid expression.
  let prev = -Infinity;
  return [0.2, 0.4, 0.6, 0.8, 1.0].map(p => {
    const v = at(p);
    const next = v > prev ? v : prev + 1;
    prev = next;
    return next;
  });
};

/** Fill-layer paint for a value-keyed choropleth. `value` is set during boundary/report merge. */
export const buildFillPaint = (features: ValueFeature[]): Record<string, unknown> => {
  const stops = getStops(features);
  // `['has', 'value']` isn't enough: every feature here carries a `value` key
  // (boundary/report merge sets it to `null`, never omits it), so `has` was
  // always true and this fallback was dead code — interpolate then choked on
  // a null input for no-data features, which Mapbox renders as a default
  // solid-black fill rather than erroring loudly. `get` returns null for a
  // missing OR null-valued property, so comparing against null catches both.
  // The CSS keyword `'transparent'` also isn't reliably honored by Mapbox GL
  // style expressions (observed rendering solid black at ~60% opacity) — use
  // an explicit rgba with zero alpha instead.
  const hasValue = ['!=', ['get', 'value'], null];
  if (!stops) {
    return {
      'fill-color': ['case', hasValue, '#D9D9D9', 'rgba(0,0,0,0)'],
      'fill-opacity': 0.6,
    };
  }
  return {
    'fill-color': [
      'case',
      hasValue,
      [
        'interpolate',
        ['linear'],
        ['get', 'value'],
        stops[0],
        RAMP_COLORS[0],
        stops[1],
        RAMP_COLORS[1],
        stops[2],
        RAMP_COLORS[2],
        stops[3],
        RAMP_COLORS[3],
        stops[4],
        RAMP_COLORS[4],
      ],
      'rgba(0,0,0,0)',
    ],
    'fill-opacity': 0.6,
  };
};
